import { createContext, useContext, useState, type ReactNode } from 'react';
import { useCopilotSession, CopilotProvider } from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useCopilotSessionAutoName, useIsFullScreenAssistantPage } from '../hooks';
import { open } from '@/features/sidebar';
import { isEmptyString } from '@/utils/string';
import { useQueryParamAction } from '@/hooks/use-query-param-action';
import {
  Box,
  Code,
  Divider,
  Emphasis,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Paragraph,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import type { MarkdownComponents } from '@customerio/hydra-copilot/react';

interface CopilotContextValue {
  session: ReturnType<typeof useCopilotSession>;
  input: string;
  setInput: (value: string) => void;
}

const CopilotSessionContext = createContext<CopilotContextValue | null>(null);

export function useCopilotSessionContext() {
  const context = useContext(CopilotSessionContext);
  if (context == null) {
    throw new Error('useCopilotSessionContext must be used within CopilotSessionProvider');
  }
  return context.session;
}

export function useCopilotChatContext() {
  const context = useContext(CopilotSessionContext);
  if (context == null) {
    throw new Error('useCopilotChatContext must be used within CopilotSessionProvider');
  }
  return context;
}

const components: MarkdownComponents = {
  p: ({ children }) => (
    <Paragraph style={{ lineHeight: 1.65 }} color="inherit">
      {children}
    </Paragraph>
  ),
  h1: ({ children }) => (
    <Heading level="2" color="inherit">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level="3" color="inherit">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level="4" color="inherit">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading level="5" color="inherit">
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const language = typeof className === 'string' ? className.replace('language-', '') : '';
    const isMultiline = typeof children === 'string' && children.includes('\n');
    const isBlock = language.length > 0 || isMultiline;

    return isBlock ? (
      <Box p="150" backgroundColor="subtle" style={{ borderRadius: themeVars.radius['6'] }} overflowX="auto">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="100">
          <Text size="xs" color="subtle" weight="medium">
            {language ?? 'plaintext'}
          </Text>
        </Box>
        <Text as="pre" style={{ margin: 0, maxWidth: '100%', overflowX: 'auto' }}>
          <Text
            as="code"
            style={{
              whiteSpace: 'pre',
              background: 'transparent',
              fontFamily: themeVars['font-family']['mono'],
              fontWeight: 'normal',
            }}
          >
            {children}
          </Text>
        </Text>
      </Box>
    ) : (
      <Code>{children}</Code>
    );
  },
  blockquote: ({ children }) => (
    <Box
      pl="200"
      as="blockquote"
      style={{
        borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['border-minimal']}`,
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => <List type="unordered">{children}</List>,
  ol: ({ children }) => <List type="ordered">{children}</List>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  a: ({ href, children }) => (
    <Link href={href} isExternal={true} variant="secondary">
      {children}
    </Link>
  ),
  img: ({ src, alt }) => <Image src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />,
  hr: () => <Divider my="100" />,
  table: ({ children }) => (
    <Box overflowX="auto">
      <Table caption="Table">{children}</Table>
    </Box>
  ),
  thead: ({ children }) => <TableThead>{children}</TableThead>,
  tbody: ({ children }) => <TableTbody>{children}</TableTbody>,
  tr: ({ children }) => <TableTr>{children}</TableTr>,
  th: ({ children }) => <TableTh>{children}</TableTh>,
  td: ({ children }) => <TableTd>{children}</TableTd>,
};

export function CopilotSessionProvider({ children }: { children: ReactNode }) {
  const session = useCopilotSession({ isPrimary: true });
  const [input, setInput] = useState('');
  const isFullScreenAssistantPage = useIsFullScreenAssistantPage();

  useCopilotSessionAutoName(session);

  // Handle prefilled message from query parameter
  useQueryParamAction('ai-message', (message) => {
    if (!isEmptyString(message)) {
      setInput(message);
      // Only open sidebar if not already on the assistant page
      if (!isFullScreenAssistantPage) {
        open('copilot');
      }
    }
  });

  const contextValue: CopilotContextValue = {
    session,
    input,
    setInput,
  };

  return (
    <CopilotSessionContext.Provider value={contextValue}>
      <CopilotProvider components={components}>{children}</CopilotProvider>
    </CopilotSessionContext.Provider>
  );
}
